import { useEffect, useState } from 'react';
import { initialsOf } from '../lib/commsFormat';
import { gravatarUrl } from '../lib/gravatar';

/**
 * One contact's face, wherever a contact is named.
 *
 * Three sources, tried in order, because no single channel covers everyone:
 *   1. `photoUrl` — a real profile photo we hold (Telegram; presigned by
 *      GET /contacts/:id, so it is null on list rows that never fetched one),
 *   2. Gravatar, for any contact with an email,
 *   3. initials on a colour derived from the name.
 *
 * Every step falls through on error rather than up-front feature detection: a
 * presigned url that expired mid-session and a Gravatar 404 look identical to
 * an <img>, and both must land on initials rather than a broken-image icon.
 */
export function ContactAvatar({
  name,
  email,
  photoUrl,
  size,
}: {
  name: string;
  email?: string | null;
  /** A stored profile photo, already presigned. */
  photoUrl?: string | null;
  size: number;
}) {
  const [gravatar, setGravatar] = useState<string | null>(null);
  // Which sources this render has ruled out. Keyed by url so a contact switch
  // (new url) starts clean without a separate reset effect.
  const [failed, setFailed] = useState<Record<string, true>>({});

  useEffect(() => {
    let live = true;
    // Retina: ask for double, the box is CSS-sized.
    void gravatarUrl(email ?? null, size * 2).then((url) => {
      if (live) setGravatar(url);
    });
    return () => {
      live = false;
    };
  }, [email, size]);

  const src = [photoUrl, gravatar].find((url) => url && !failed[url]) ?? null;
  const box = { width: size, height: size, fontSize: Math.round(size * 0.36) };

  if (src) {
    return (
      <img
        className="comms-avatar comms-avatar-img"
        style={box}
        src={src}
        alt=""
        loading="lazy"
        // Gravatar answers 404 for an address it has never seen (d=404), which
        // is the whole point — it means "no face", not "broken".
        onError={() => setFailed((prev) => ({ ...prev, [src]: true }))}
      />
    );
  }
  return (
    <div className="comms-avatar" style={{ ...box, ...tintFor(name) }}>
      {initialsOf(name)}
    </div>
  );
}

/**
 * A stable colour per person, so the same contact is the same swatch in the
 * list, the header and the panel — with no palette stored anywhere. Hue only:
 * saturation and lightness are fixed per theme by the CSS variables below, so
 * every tint stays legible in both.
 */
function tintFor(name: string) {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) % 360;
  }
  return {
    background: `hsl(${hash} var(--avatar-sat) var(--avatar-bg-l))`,
    color: `hsl(${hash} var(--avatar-sat) var(--avatar-fg-l))`,
  };
}
